import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import {
  ErrorCode,
  ErrorService,
  SystemCriticalError,
} from '@trustedshops/tswp-carrier-core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { BootstrapErrorLogoutService } from './bootstrap-error-logout.service';

interface Translations {
  'error.title': string;
  'error.description': string;
  'error.code': string;
  'error.apology': string;
  'error.button.support': string;
  'error.support.text': string;
  'error.button.logout': string;
  'error.button.loggingOut': string;
}

/**
 * Type guard to check if the value from hasError$ is a SystemCriticalError.
 * @param error The value from hasError$ observable (false | SystemCriticalError)
 * @returns true if error is a SystemCriticalError instance, false otherwise
 */
function isSystemCriticalError(
  error: false | SystemCriticalError
): error is SystemCriticalError {
  return error instanceof SystemCriticalError;
}

@Component({
  selector: 'app-bootstrap-error',
  standalone: false,
  templateUrl: './bootstrap-error.component.html',
  styleUrls: ['./bootstrap-error.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BootstrapErrorComponent implements OnInit {
  public translations: Translations = {
    'error.title': 'Something went wrong',
    'error.description': 'Please reload the page or try again later.',
    'error.code': 'Error Code: {{errorCode}}',
    'error.apology': 'We apologize for any inconvenience this may cause.',
    'error.button.support': 'contact support',
    'error.support.text': 'If this keeps happening, please',
    'error.button.logout': 'Sign out',
    'error.button.loggingOut': 'Logging out…',
  };

  public errorCodeText = `Error Code: ${ErrorCode.BootstrapError}`;
  public stackTrace: string | undefined;
  public copyButtonText = 'Copy Stack Trace';
  public isCopied = false;
  public showErrorDetails = false;
  public isLoggingOut = false;

  public constructor(
    private readonly errorService: ErrorService,
    private readonly logoutService: BootstrapErrorLogoutService
  ) {}

  /**
   * Handles refresh button click - reloads the page
   */
  public onRefresh(): void {
    window.location.reload();
  }

  /**
   * Handles sign-out button click. Independent of the failed app's DI graph:
   * BootstrapErrorLogoutService talks to Keycloak directly. Navigates away on
   * both the no-confirmation and the fallback path.
   */
  public async onLogout(): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    try {
      await this.logoutService.logout();
    } catch (error) {
      console.error('[BootstrapErrorComponent] Logout failed:', error);
      this.isLoggingOut = false;
    }
  }

  /**
   * Copies the stack trace to clipboard
   */
  public async copyStackTrace(): Promise<void> {
    if (!this.stackTrace) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.stackTrace);
      this.isCopied = true;
      this.copyButtonText = 'Copied!';

      // Reset button text after 2 seconds
      setTimeout(() => {
        this.isCopied = false;
        this.copyButtonText = 'Copy Stack Trace';
      }, 2000);
    } catch (error) {
      console.error(
        '[BootstrapErrorComponent] Failed to copy stack trace:',
        error
      );
      // Fallback for older browsers
      this.fallbackCopyToClipboard(this.stackTrace);
    }
  }

  /**
   * Fallback method for copying to clipboard (for older browsers)
   */
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.isCopied = true;
      this.copyButtonText = 'Copied!';
      setTimeout(() => {
        this.isCopied = false;
        this.copyButtonText = 'Copy Stack Trace';
      }, 2000);
    } catch (error) {
      console.error('[BootstrapErrorComponent] Fallback copy failed:', error);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  public async ngOnInit(): Promise<void> {
    // Check URL parameter to show error details - show if 'debug' parameter exists (regardless of value)
    const urlParams = new URLSearchParams(window.location.search);
    this.showErrorDetails = urlParams.has('debug');

    // Get error from ErrorService to extract error code and stack trace
    try {
      const error = await firstValueFrom(
        this.errorService.hasError$.pipe(take(1))
      );

      if (isSystemCriticalError(error) && error.stack) {
        this.stackTrace = error.stack;
      }
    } catch (error) {
      console.warn(
        '[BootstrapErrorComponent] Error retrieving error state:',
        error
      );
      // Error service might not have error yet, continue with defaults
    }

    // Get error code from ErrorService if available
    const errorCode = this.errorService.errorCode || ErrorCode.BootstrapError;

    // Use hardcoded translations and replace error code
    this.errorCodeText = this.translations['error.code'].replace(
      '{{errorCode}}',
      errorCode
    );
  }
}
